import { useEffect, useState } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { createClient } from '@/lib/supabase/client'
import { isAuthNetworkError } from '@/lib/offline'

/**
 * The route gate — what `middleware.ts` + `lib/supabase/middleware.ts` did.
 *
 * ⚠️ THIS WAS NEVER THE SECURITY BOUNDARY AND STILL IS NOT. RLS is (see
 * migrations 051 and 057). A gate that runs in the browser can be stepped
 * over by anyone willing to open devtools, and it would gain them nothing:
 * every table answers `org_id = auth_org()` and the accounting tables answer
 * `auth_is_owner()`. What this does is keep a signed-out person from staring
 * at an empty shell, which is a usability job, not a security one.
 *
 * The old middleware's cheap-cookie short-circuit is deliberately NOT ported.
 * It existed to avoid spending Worker CPU on `@supabase/ssr` during Error 1102
 * — see the comment it carried. There is no Worker in this path any more: the
 * SPA is static files off the CDN, so the reason for that complexity is gone
 * with it.
 */
export function RequireSession() {
  const location = useLocation()
  const [state, setState] = useState<'checking' | 'in' | 'out'>('checking')

  useEffect(() => {
    let alive = true
    const supabase = createClient()

    // `await`, not `.then`, to match the rest of the codebase — and because
    // `createClient()` can return the not-configured Proxy, whose type makes
    // the callback's parameters infer as `any` under `strict`.
    ;(async () => {
      const { data, error } = await supabase.auth.getUser()
      if (data.user) { if (alive) setState('in'); return }
      // OFFLINE: getUser() asks the server, and with no connection it answers
      // „nobody" — so an offline reload was sent to /login, and the till's
      // offline copy (the whole point of it) was never reached. Found on
      // 2026-09-26 with a 10 000-product test; broken since the Vite cutover.
      // With no network, the session stored on this device is the best
      // answer there is — and the gate is usability, not security (above).
      if (!navigator.onLine || isAuthNetworkError(error)) {
        const { data: local } = await supabase.auth.getSession()
        if (alive) setState(local.session?.user ? 'in' : 'out')
        return
      }
      if (alive) setState('out')
    })()

    // A session can end while the app is open — a token expiring overnight, or
    // the owner signing out on another tab of the same till.
    const { data: sub } = supabase.auth.onAuthStateChange(
      (_event: string, session: { user?: unknown } | null) => {
        if (alive) setState(session?.user ? 'in' : 'out')
      },
    )
    return () => { alive = false; sub.subscription.unsubscribe() }
  }, [])

  if (state === 'checking') {
    return (
      <div className="flex h-dvh w-full items-center justify-center bg-background">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (state === 'out') {
    // Remember where they were headed so login can send them back.
    return <Navigate to="/login" replace state={{ from: location.pathname }} />
  }

  return <Outlet />
}
